import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Grid2, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ChemistryRow } from "@/pages/chemistries/report/models/ChemistryRow";
import { SupplierLegend } from "@/pages/chemistries/shared/components/SupplierLegend";
import { CampaignResponse } from "@/pages/mill/schedule/models/CampaignReponse";
import { useAssetId } from "@/setup/hooks/useAssetId";
import { DEFAULT_MILL } from "@/setup/mill-config/mill-config";
import { DelayAreaSelector } from "@/shared/selectors/DelayAreaSelector";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { LastChemistriesTable } from "./components/LastChemistriesTable";
import { PendingHeatsTable } from "./components/PendingHeatsTable";
import {
	getLastChemistries,
	getPendingHeats,
} from "./repositories/ChemistriesRepository";

export const usePendingHeats = ({
	areaId,
	onError,
}: {
	areaId: number;
	onError: (error: Error) => void;
}) => {
	return useQuery<CampaignResponse[], Error>({
		queryKey: ["pendingHeats", areaId],
		queryFn: ({ signal }) => getPendingHeats(areaId, signal),
		refetchInterval: 60_000,
		onError,
	});
};

export const useLastChemistries = ({
	onError,
}: {
	onError: (error: Error) => void;
}) => {
	return useQuery<ChemistryRow[], Error>({
		queryKey: ["lastChemistries"],
		queryFn: ({ signal }) => getLastChemistries(signal),
		refetchInterval: 60_000,
		onError,
	});
};

export const ChemistriesPage = () => {
	const assetId = useAssetId();

	const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
	const [error, setError] = useState("");

	const areaId = selectedAreaId ?? assetId ?? DEFAULT_MILL.id;

	const { data: heats, isLoading: isLoadingHeats } = usePendingHeats({
		areaId,
		onError: (error) => setError(error.message),
	});

	const { data: chemistries, isLoading: isLoadingChemistries } =
		useLastChemistries({
			onError: (error) => setError(error.message),
		});

	return (
		<>
			<HelmetDexteel title="Chemistries" />
			<Grid2
				container
				direction="column"
				spacing={1}
				p={2}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2>
					<Typography variant="h5" fontWeight={600}>
						Chemistries
					</Typography>
				</Grid2>

				<Grid2>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						flexWrap="wrap"
						gap={1}
					>
						<Typography variant="subtitle1" fontWeight={600}>
							{`Last 10 heat chemistries`}
						</Typography>
						<SupplierLegend rows={chemistries ?? []} />
					</Stack>
				</Grid2>

				<Grid2>
					<LastChemistriesTable
						rows={chemistries ?? []}
						isLoading={isLoadingChemistries}
					/>
				</Grid2>

				<Grid2 />

				<Grid2>
					<Typography variant="subtitle1" fontWeight={600}>
						Heats pending disposition
					</Typography>
				</Grid2>

				<Grid2 container>
					<Grid2 size={{ xs: 12, md: 2 }}>
						<DelayAreaSelector
							label="Mill"
							value={areaId}
							onChange={(newAreaId: number | null) =>
								setSelectedAreaId(newAreaId)
							}
						/>
					</Grid2>
				</Grid2>

				<Grid2>
					<PendingHeatsTable rows={heats ?? []} isLoading={isLoadingHeats} />
				</Grid2>
			</Grid2>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
